/**
 * RD_OBS_Autism.js — Autism Behavioural Observation Review Document
 *
 * Clinician-facing paragraph review page for the Autism OBS module. Mirrors
 * RD_SSI_Autism.js (same modular subcollection architecture, same
 * approve/edit/reset/skip transactions on reportContent/observation, same
 * exit-approval flow) but with OBS-specific document structure.
 *
 * ─── DOCUMENT STRUCTURE ──────────────────────────────────────────────────────
 *
 * The Observation report is composed of up to 9 paragraphs across three sections,
 * in the same order as the Observation proforma (Criterion Mapping sidenav):
 *
 *   1. "DSM-5 Criteria — Behavioural Observation" — 7 criterion paragraphs,
 *      one per DSM-5 criterion (A1-A3 social communication, B1-B4 restricted/
 *      repetitive behaviours). Each aggregates evidence from all observation
 *      tasks that contributed observations for that criterion.
 *
 *   2. "Self-Report" — 1 paragraph summarising the patient's self-reported
 *      experiences during the observation session. Deletable during review.
 *
 *   3. "Other Behaviours" — 1 OPTIONAL paragraph (v2 tagged-notes redesign
 *      only) summarising observed behaviours not mapped to a specific DSM-5
 *      criterion. Only present when the clinician tagged something to the
 *      catch-all "Other Behaviours" bucket; absent (and filtered out) otherwise,
 *      so it never appears or counts for v1 patients. Deletable during review.
 *
 * Paragraph codes follow the backend naming convention:
 *   - Criteria:        asd_obs_A1 .. asd_obs_B4
 *   - Other Behaviours: asd_obs_other_behaviours
 *   - Self-Report:     asd_obs_self_report
 *
 * Data source, approval tracking, Firestore writes, navigation, and the
 * absence of PDF rendering / completion emails all match RD_SSI_Autism.js.
 * See that file for the architectural rationale shared across Autism RD pages.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useModularApprovalTracking } from '../../../hooks/useModularApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeModularSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { ParagraphDeleteDialog } from './components/ParagraphDeleteDialog';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { buildParagraphDeletionWrites } from '../../../utils/reviewParagraphDeletion';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { renderModelInput } from '../../../utils/renderModelInput';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { STAGE_TO_CLINICIAN_KEY } from '../../../constants/adultAutismStages';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerRenderAutismDocTemplate } from '../../../utils/TriggerRenderAutismDocTemplate';
import { rerenderAutismPatientReportIfStale } from '../../../utils/autoAutismPatientReport';
import { db, auth } from '../../../firebase';
import { doc as firestoreDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../../../constants/adultAutismFirestorePaths';
import './styles/ReviewDocuments.css';
import {
  emitParagraphApproval,
  emitParagraphDeleted,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

// ─── SECTION CONSTANTS ────────────────────────────────────────────────────────
// Exported for independent testing. Each section key is the heading shown to the
// clinician; the inner map has a single entry "1" because each OBS criterion
// produces one unified paragraph (no Childhood/Adulthood split).

/**
 * DSM-5 Criterion paragraphs for the Autism Behavioural Observation.
 * Domain A (social communication deficits): 3 items (A1-A3)
 * Domain B (restricted/repetitive behaviours): 4 items (B1-B4)
 */
export const ASD_OBS_CRITERION_SECTIONS = {
  'Criterion A1: Social-Emotional Reciprocity': {
    '1': { title: 'Social-Emotional Reciprocity', code: 'asd_obs_A1' },
  },
  'Criterion A2: Nonverbal Communicative Behaviours': {
    '1': { title: 'Nonverbal Communicative Behaviours', code: 'asd_obs_A2' },
  },
  'Criterion A3: Developing, Maintaining, and Understanding Relationships': {
    '1': { title: 'Relationships', code: 'asd_obs_A3' },
  },
  'Criterion B1: Stereotyped or Repetitive Motor Movements, Use of Objects, or Speech': {
    '1': { title: 'Stereotyped/Repetitive Behaviours', code: 'asd_obs_B1' },
  },
  'Criterion B2: Insistence on Sameness, Inflexible Adherence to Routines': {
    '1': { title: 'Insistence on Sameness', code: 'asd_obs_B2' },
  },
  'Criterion B3: Highly Restricted, Fixated Interests': {
    '1': { title: 'Restricted Interests', code: 'asd_obs_B3' },
  },
  'Criterion B4: Hyper- or Hyporeactivity to Sensory Input': {
    '1': { title: 'Sensory Reactivity', code: 'asd_obs_B4' },
  },
};

/**
 * Backend paragraph code for the Self-Report paragraph. The clinician may
 * delete this one paragraph during review (mirroring ADHD SSI Strengths); the
 * seven DSM-5 criterion paragraphs are not deletable.
 */
export const ASD_OBS_SELF_REPORT_CODE = 'asd_obs_self_report';

/**
 * Self-Report section — a single paragraph summarising the patient's own
 * account of their experiences during the observation session.
 */
export const ASD_OBS_SELF_REPORT_SECTION = {
  'Self-Report': {
    '1': { title: 'Self-Reported Experiences', code: ASD_OBS_SELF_REPORT_CODE },
  },
};

/**
 * Backend paragraph code for the Other Behaviours paragraph (v2 tagged-notes
 * redesign only). Like Self-Report it is optional and deletable. It only ever
 * has content for a v2 patient who tagged an observation to the catch-all
 * "Other Behaviours" bucket — for v1 patients (and v2 patients who tagged
 * nothing 'other') the backend never writes this output, so the empty-output
 * filters in computeValidCodes hide the section entirely and it never counts
 * towards the approval denominator.
 */
export const ASD_OBS_OTHER_BEHAVIOURS_CODE = 'asd_obs_other_behaviours';

/**
 * Other Behaviours section — a single paragraph summarising clinically relevant
 * behaviours the clinician did not map to a specific DSM-5 criterion. Rendered
 * after Self-Report so the AI-outputs review matches Criterion Mapping order
 * (A1–B4 → Self-Reported → Other Behaviours).
 */
export const ASD_OBS_OTHER_BEHAVIOURS_SECTION = {
  'Other Behaviours': {
    '1': { title: 'Other Behaviours', code: ASD_OBS_OTHER_BEHAVIOURS_CODE },
  },
};

/** True only for the deletable Self-Report paragraph. */
export const isSelfReportParagraph = (code) => code === ASD_OBS_SELF_REPORT_CODE;

/** True only for the deletable Other Behaviours paragraph. */
export const isOtherBehavioursParagraph = (code) =>
  code === ASD_OBS_OTHER_BEHAVIOURS_CODE;

/**
 * True for any paragraph the clinician is allowed to delete during review.
 * The seven DSM-5 criterion paragraphs are never deletable; only the two
 * optional narrative paragraphs (Self-Report, Other Behaviours) are.
 */
export const isDeletableParagraph = (code) =>
  isSelfReportParagraph(code) || isOtherBehavioursParagraph(code);

/**
 * Per-deletable-paragraph confirmation dialog copy and the error toast id used
 * on a failed delete write. Keyed by paragraph code so the dialog names the
 * exact paragraph being removed (calmer, honest UX) and each failure surfaces a
 * distinct toast. Self-Report copy is kept verbatim for backwards parity.
 */
export const DELETABLE_PARAGRAPH_DIALOG = {
  [ASD_OBS_SELF_REPORT_CODE]: {
    title: 'Delete Self-Reported Experiences',
    description:
      'This paragraph will not appear in the Behavioural Observation report or the Patient Report. It can come back if the self-report notes change in the proforma.',
    errorToastId: 'self-report-delete-error',
    errorMessage: 'The paragraph could not be deleted. Please try again.',
  },
  [ASD_OBS_OTHER_BEHAVIOURS_CODE]: {
    title: 'Delete Other Behaviours',
    description:
      'This paragraph will not appear in the Behavioural Observation report or the Patient Report. It can come back if you tag another observation as an other behaviour.',
    errorToastId: 'other-behaviours-delete-error',
    errorMessage: 'The paragraph could not be deleted. Please try again.',
  },
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function ReviewDocumentOBS_Autism() {
  const { patientId } = useParams();
  const { claims } = useAuth();
  const navigate = useNavigate();

  const MODULE_TITLE = 'Behavioural Observation';
  const MODULE_PREFIX_CODE = 'obs';

  // ─── Context: subcollection-based data ──────────────────────────────────────
  const {
    header,
    reportContent,
    updateReportContent,
    refreshReportContent,
    clearPatientData,
    fetchPatientData,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // Guard against deep links: for workflows that do not run Behavioural
  // Observation (e.g. LMP) this page has no meaningful content, so once the
  // patient header has loaded and confirms Observation is disabled, bounce the
  // clinician back to the Autism documents dashboard (the same place the
  // exit-approval flow lands). We wait for the header to load (loadedPatientId
  // matches) so we never redirect on a transient null config.
  const wf = useWorkflowConfig(header, claims);
  const isObservationEnabled = wf?.isObservationEnabled !== false;
  useEffect(() => {
    if (loadedPatientId === patientId && !isObservationEnabled) {
      navigate(`/patient/${patientId}/adult-autism/documents`, { replace: true });
    }
  }, [loadedPatientId, patientId, isObservationEnabled, navigate]);

  // ─── Edit permission gate ───────────────────────────────────────────────────
  // Only the clinician assigned as primary for the Observation module may
  // review, edit, and approve these AI paragraphs. Every other provider who can
  // open the Documents Dashboard (other module clinicians, org admins, DD
  // supporting clinicians) lands here read-only — they can browse paragraphs
  // and AI inputs but cannot edit, approve, reset, or finalise. Defence in
  // depth behind the dashboard's assignee-gated Review button so a deep link
  // cannot become an editing back door. Reads the same
  // `header.clinicians.clinicianOBS` slot the dashboard uses.
  const canEdit = useCanEditStage(header, 'OBS');
  const assignedClinicianName =
    header?.clinicians?.[STAGE_TO_CLINICIAN_KEY.observation]?.clinicianName;

  // ─── Derived data from the OBS reportContent document ───────────────────────
  const obsData = reportContent?.observation || {};
  const REPORT_DATA_OUTPUTS = useMemo(() => obsData.outputs || {}, [obsData.outputs]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    obsData.editHistory || {},
    [obsData.editHistory]);
  const REPORT_DATA_INPUTS = useMemo(() => obsData.inputs || {}, [obsData.inputs]);
  const REPORT_DATA_APPROVED = useMemo(() => obsData.approvedParagraphs || {}, [obsData.approvedParagraphs]);

  // ─── Document structure ─────────────────────────────────────────────────────
  // Matches Observation proforma order: A1–B4 → Self-Report → Other Behaviours.
  // Other Behaviours is filtered out when empty (v1 / unused), so v1 patients
  // still see Criteria then Self-Report only.
  const docStructure = useMemo(() => ({
    ...ASD_OBS_CRITERION_SECTIONS,
    ...ASD_OBS_SELF_REPORT_SECTION,
    ...ASD_OBS_OTHER_BEHAVIOURS_SECTION,
  }), []);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    clinicianInputs: null,
    clinicianDecisions: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [initialContent, setInitialContent] = useState({});
  const [preEditContent, setPreEditContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // Tracks the Self-Report code if the clinician deletes it this session. Fed
  // into useModularApprovalTracking AND finalizeModularSectionReview as
  // excludedCodes so the on-screen denominator and the persisted approval flag
  // cannot disagree. The Firestore listener will make this permanent (empty
  // output => filtered out everywhere) once the write round-trips; deletedCodes
  // just keeps the UI correct in the intervening window.
  const [deletedCodes, setDeletedCodes] = useState(new Set());

  // Confirmation dialog state for the Self-Report "Delete" action.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteCode, setPendingDeleteCode] = useState(null);

  // ─── Approval tracking via modular hook ─────────────────────────────────────
  const {
    totalParagraphs,
    reviewedCodes,
    isFullyApproved,
    showWarning,
    addReviewedCode,
    removeReviewedCode,
  } = useModularApprovalTracking({
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    reportDataApproved: REPORT_DATA_APPROVED,
    excludedCodes: deletedCodes,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ─── Snapshot initial content on first load ─────────────────────────────────
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS && Object.keys(REPORT_DATA_OUTPUTS).length > 0) {
      const initialValues = {};
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== '') {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // ─── Refs and floating panel ────────────────────────────────────────────────
  const documentContainerRef = useRef(null);
  const editableContentRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  // ─── Firestore doc reference for the OBS reportContent document ─────────────
  const getDocRef = useCallback(() => {
    const reportContentPath = getPatientReportContentPathSegments(patientId, 'observation');
    return firestoreDoc(db, ...reportContentPath);
  }, [patientId]);

  // ─── Paragraph click handler ────────────────────────────────────────────────
  // Tolerates missing inputs (a criterion with no evidence still needs to be
  // selectable and approvable; the AI Inputs panel just shows empty).
  const handleParagraphClick = (event, code, content) => {
    setIsEditing(false);

    const inputs = REPORT_DATA_INPUTS?.[code] || {};
    setState(prevState => ({
      ...prevState,
      selectedCode: code,
      textareaContent: content || ' ',
      clinicianInputs: inputs['Clinician Notes'],
      clinicianDecisions: inputs['Clinician Decisions'],
      isSidePanelOpen: true,
    }));

    setTimeout(() => {
      safeUpdatePosition(code);
    }, 0);
  };

  // ─── Section component ──────────────────────────────────────────────────────
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ''
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1 text-blue-500">{title}</h2>
        <div className="mb-3" />

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content);
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                  relative text-sm font-medium p-2 rounded-lg
                  transition-all duration-200
                  ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                  ${state.selectedCode === code ? 'ring ring-violet-800' : ''}
                  ${!isEditing ? 'cursor-pointer' : ''}
                `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');
                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Edit handlers ──────────────────────────────────────────────────────────

  const handleStartEdit = () => {
    if (!canEdit) return;
    if (state.selectedCode) {
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${CSS.escape(state.selectedCode)} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Observation module can edit these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const spanCode = state.selectedCode;
    const newValue = REPORT_DATA_OUTPUTS[spanCode];
    const previousValue = initialContent[spanCode] || '';

    if (newValue !== previousValue) {
      removeReviewedCode(state.selectedCode);
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);

      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        editType: 'manual',
      };
      const timestampKey = Date.now().toString();

      // Targeted per-key updateDoc (root fields on this stage's reportContent
      // subcollection doc) instead of a read-modify-write runTransaction. The
      // transaction's document-version precondition contends under slow 3G/4G
      // and silently discards the edit; a plain updateDoc on distinct field
      // paths does not. See handleParagraphApproval for the full rationale.
      const docRef = getDocRef();

      const startedAt = performance.now();
      updateDoc(docRef, {
        // Persist the edited text
        [`outputs.${spanCode}`]: newValue,
        // Editing invalidates any prior approval
        [`approvedParagraphs.${spanCode}`]: deleteField(),
        // Append an audit entry under this paragraph's edit history
        [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      }).then(() => {
        // Confirms the edit persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
        // Analytics mirror of the edit that just persisted: paragraph code,
        // section and the two lengths only — never the wording itself.
        emitParagraphEdited({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
          prevText: previousValue,
          priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
          nextText: newValue,
        });
      }).catch(error => {
        console.error('Error updating paragraph approval on edit:', error);
        // Roll back the optimistic un-approval and surface the failure so
        // the clinician does not believe their edit was saved.
        addReviewedCode(state.selectedCode);
        toast.error(
          'Your edit did not save. Please try editing the paragraph again.',
          { toastId: 'paragraph-edit-save-error' }
        );
      });

      setSectionDirty(true);
    } else {
      setIsEditing(false);
      safeUpdatePosition(state.selectedCode);
    }
  };

  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Observation module can reset these paragraphs.');
      return;
    }
    if (!state.selectedCode || !initialContent[state.selectedCode]) return;

    const spanCode = state.selectedCode;
    const newValue = initialContent[spanCode];
    const previousValue = REPORT_DATA_OUTPUTS[spanCode];

    if (REPORT_DATA_OUTPUTS) {
      REPORT_DATA_OUTPUTS[spanCode] = newValue;
    }
    removeReviewedCode(spanCode);

    const editEntry = {
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      isReset: true,
    };
    const timestampKey = Date.now().toString();

    // Targeted per-key updateDoc (root fields) instead of a read-modify-write
    // runTransaction, which contends on the doc version under slow 3G/4G and
    // loses the reset. See handleSaveEdit / handleParagraphApproval.
    const docRef = getDocRef();

    const startedAt = performance.now();
    updateDoc(docRef, {
      // Restore the original generated text
      [`outputs.${spanCode}`]: newValue,
      // A reset also invalidates any prior approval
      [`approvedParagraphs.${spanCode}`]: deleteField(),
      // Record the reset in this paragraph's edit history
      [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
      // Review metadata (mirrors the approval write)
      lastReviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
      reviewedByName: auth.currentUser.displayName,
    }).then(() => {
      // Confirms the reset persisted AND the paragraph was un-approved
      // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
      // Analytics mirror of the reset that just persisted (code only).
      emitParagraphReset({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code: spanCode,
      });
    }).catch(error => {
      console.error('Error resetting paragraph:', error);
      // Roll back both content and approval, then surface the failure so the
      // clinician knows the reset did not persist.
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[spanCode] = previousValue;
      }
      addReviewedCode(spanCode);
      toast.error(
        'The paragraph reset did not save. Please try again.',
        { toastId: 'paragraph-reset-save-error' }
      );
    });

    setIsEditing(false);
    safeUpdatePosition(spanCode);
    setSectionDirty(true);
  };

  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  // ─── Delete an optional paragraph (Self-Report or Other Behaviours) ─────────
  // Opens the confirmation dialog. The actual deletion is deferred to
  // executeDeleteParagraph so the clinician can cancel without side-effects.
  const handleDeleteParagraph = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Observation module can delete this paragraph.');
      return;
    }
    if (!state.selectedCode || !isDeletableParagraph(state.selectedCode)) return;
    setPendingDeleteCode(state.selectedCode);
    setShowDeleteConfirm(true);
  };

  // Performs the deletion after the clinician confirms. Mirrors the ADHD SSI
  // Strengths delete: clears the output to "" (so every empty-string filter
  // hides it), deleteField()s the approval, and appends an { isDeleted: true }
  // audit entry — all via the shared buildParagraphDeletionWrites payload.
  //
  // Two deliberate divergences from ADHD strengths:
  //   1. No paired title code exists for these paragraphs (ADHD clears
  //      pl_strength_N alongside pl_strength_N_summary), so alsoClearCodes is empty.
  //   2. REPORT_DATA_OUTPUTS is a useMemo over the LIVE context object, so
  //      mutating it in place does NOT itself trigger a re-render. The re-render
  //      comes from setDeletedCodes; the denominator recompute comes from the
  //      excludedCodes Set changing identity. Do not "clean up" this mutation
  //      into local state — the optimistic UI relies on both effects.
  //
  // If OBS ever gains a dashboard "Regenerate AI outputs" button, a
  // force_regenerate_keys: ['__ALL__'] run would resurrect a deleted paragraph;
  // that is acceptable (a resurrected paragraph resets approval and returns to
  // review), but worth knowing.
  const executeDeleteParagraph = () => {
    const code = pendingDeleteCode;
    if (!code) return;

    // Per-paragraph copy (falls back to the Self-Report entry for safety).
    const dialogCopy =
      DELETABLE_PARAGRAPH_DIALOG[code] ||
      DELETABLE_PARAGRAPH_DIALOG[ASD_OBS_SELF_REPORT_CODE];

    const previousValue = REPORT_DATA_OUTPUTS[code];
    const wasReviewed = reviewedCodes.has(code);
    const timestampKey = Date.now().toString();

    // --- Optimistic UI ---
    if (REPORT_DATA_OUTPUTS) {
      REPORT_DATA_OUTPUTS[code] = '';
    }
    setDeletedCodes(prev => new Set([...prev, code]));
    removeReviewedCode(code);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      isSidePanelOpen: false,
      clinicianInputs: null,
      clinicianDecisions: null,
    }));
    setShowDeleteConfirm(false);
    setPendingDeleteCode(null);

    // --- Firestore write (targeted per-key updateDoc, not a transaction) ---
    // Same rationale as handleSaveEdit / handleParagraphApproval: distinct field
    // paths, so no document-version contention on slow links.
    const docRef = getDocRef();
    const payload = buildParagraphDeletionWrites({
      code,
      previousValue,
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestampKey,
    });

    const startedAt = performance.now();
    updateDoc(docRef, payload).then(() => {
      // Confirms the deletion persisted. Code-only, no PII.
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(`[delete] deleted "${code}" in ${elapsedMs}ms`);
      // Analytics mirror of the deletion that just persisted (code only).
      emitParagraphDeleted({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
      });
    }).catch(error => {
      console.error('Error deleting paragraph:', error);
      // Roll back ALL optimistic updates and surface the failure.
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[code] = previousValue;
      }
      setDeletedCodes(prev => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      if (wasReviewed) addReviewedCode(code);
      toast.error(
        dialogCopy.errorMessage,
        { toastId: dialogCopy.errorToastId }
      );
    });

    setSectionDirty(true);
  };

  // ─── Paragraph approval ─────────────────────────────────────────────────────
  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Observation module can approve these paragraphs.');
      return;
    }
    const docRef = getDocRef();

    try {
      // Targeted per-key write instead of a full read-modify-write transaction
      // on approvedParagraphs — avoids document-version contention when a
      // clinician approves many paragraphs quickly on slow 3G/4G links (see
      // RD_CI.js for the full rationale). `code` is a dot-free identifier and
      // the fields below are siblings, so there is no conflicting field-path
      // rejection.
      const updateData = {
        [`approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      };

      // Timed, PII-free confirmation log (updateDoc resolves on server ack).
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'autism',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error('Error updating paragraph approval:', error);
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // reports finalising with missing / unapproved content.
      throw error;
    }
  };

  // ─── Mark as Done (approve + advance) ──────────────────────────────────────
  const handleMarkAsDone = async () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the Observation module can approve these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (!paragraphElement) return;

    try {
      // Track the in-flight write so Save & Exit awaits it before finalising.
      trackApproval(
        handleParagraphApproval(state.selectedCode, true).catch(error => {
          console.error('Error in handleParagraphApproval:', error);
          // Roll back the optimistic update and tell the clinician — a
          // silently failed approval is exactly how incomplete sections
          // used to slip through to the rendered report.
          removeReviewedCode(state.selectedCode);
          toast.error(
            'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
            { toastId: 'paragraph-approval-save-error' }
          );
        })
      );

      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      addReviewedCode(state.selectedCode);

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();
        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;
          const inputs = REPORT_DATA_INPUTS[nextCode] || {};
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: inputs['Clinician Notes'],
            clinicianDecisions: inputs['Clinician Decisions'],
          }));
          setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
        }, 300);
      } else {
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
        }));
      }
    } catch (error) {
      console.error('Error in handleMarkAsDone:', error);
    }
  };

  // ─── Skip (advance without approving) ──────────────────────────────────────
  const handleSkip = () => {
    if (!canEdit) return;
    if (!state.selectedCode) return;

    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
    const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
      index > currentIndex && !reviewedCodes.has(p.id)
    );

    if (nextUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = nextUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = nextUnreviewed.id;
        const inputs = REPORT_DATA_INPUTS[nextCode] || {};
        setState(prev => ({
          ...prev,
          selectedCode: nextCode,
          isSidePanelOpen: true,
          clinicianInputs: inputs['Clinician Notes'],
          clinicianDecisions: inputs['Clinician Decisions'],
        }));
        setTimeout(() => { safeUpdatePosition(nextCode); }, 0);
      }, 300);
    } else {
      setState(prev => ({
        ...prev,
        selectedCode: null,
        isSidePanelOpen: false,
        clinicianInputs: null,
        clinicianDecisions: null,
      }));
    }
  };

  // ─── Go to first un-reviewed ───────────────────────────────────────────────
  const goToFirstUnreviewed = () => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  // ─── Exit review ───────────────────────────────────────────────────────────
  // Writes the obs* approval-status fields, triggers PDF/DOCX rendering when
  // fully approved, and navigates to the documents dashboard.
  const handleExitReview = async () => {
    // Read-only viewers (not the Observation primary) can leave the page, but
    // must never run the finalise path: no approval/status writes, no audit
    // entry, and no PDF/patient-report render. Just drop cached data and
    // navigate back to the dashboard.
    if (!canEdit) {
      // Never act on assignment until the patient doc has loaded for THIS
      // patient. canEdit starts false and only becomes true once `header`
      // hydrates, so a legitimate assignee who clicks before that would
      // otherwise be treated as read-only and navigated away without
      // finalising. Once loaded, canEdit is authoritative.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        return;
      }
      clearPatientData();
      navigate(`/patient/${patientId}/adult-autism/documents`);
      return;
    }

    setIsSaving(true);

    try {
      // Guard: never finalise against a document that has not fully loaded for
      // THIS patient. If reportContent is empty/stale the approval denominator
      // is wrong, which could either write a false "approved" flag with
      // unapproved content behind it, or revert a previously-approved flag.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        setIsSaving(false);
        return;
      }

      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot. This replaces writing the client's optimistic
      // isFullyApproved, which could diverge from Firestore. First-approval is
      // likewise snapshot-derived (write-once, correct across re-approvals).
      // Note the stageKey ('observation') differs from the flag prefix ('obs').
      const { isFullyApproved: approvedNow, outstandingCodes, wasFirstApproval } =
        await finalizeModularSectionReview({
          patientId,
          stageKey: 'observation',
          flagPrefix: MODULE_PREFIX_CODE,
          docStructure,
          // Codes deleted this session are excluded from the denominator so a
          // fully-approved remainder still finalises. The Firestore snapshot
          // read inside the transaction already sees the empty output, so this
          // only matters for the brief window before that write round-trips.
          excludedCodes: deletedCodes,
        });

      // Reconcile the optimistic UI against the authoritative snapshot. Stay on
      // the page when phantom approvals exist (green on screen but the write
      // failed) or when the snapshot found outstanding content.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await refreshReportContent(patientId, 'observation');
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: wasFirstApproval,
      });

      // Kick off PDF/DOCX rendering of the OBS report when all paragraphs are
      // approved (snapshot-derived) and something changed this session.
      if (approvedNow && sectionDirty) {
        TriggerRenderAutismDocTemplate({
          patientId,
          stageKey: 'observation',
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error triggering autism OBS render:', err));
      }

      // Auto-finalise the combined Patient Report. Called unconditionally (fire-and-forget):
      // the helper reads fresh Firestore state and only (re)renders when all five section
      // reports are approved AND the patient report is stale — mirroring the ADHD flow, so
      // there is no manual "Generate" button. Safe even on a no-op exit.
      rerenderAutismPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid })
        .catch((err) => console.error('Autism PR auto-finalise error:', err));

      clearPatientData();
      setSectionDirty(false);
      navigate(`/patient/${patientId}/adult-autism/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Progress calculation ──────────────────────────────────────────────────
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // ─── Side effects: body overflow lock ──────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ─── Side effects: scroll during edit mode ─────────────────────────────────
  useEffect(() => {
    const currentContainer = documentContainerRef.current;
    if (isEditing && currentContainer) {
      const originalOverflow = currentContainer.style.overflow;
      currentContainer.style.overflow = 'auto';

      const handleWheel = (e) => { e.stopPropagation(); };
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  // ─── Side effects: deselect on background click ────────────────────────────
  const handlePageClick = useCallback((event) => {
    if (isEditing) return;
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false,
    }));
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) {
      container.addEventListener('click', handlePageClick);
    }
    return () => {
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // Suppress unused-variable warnings for parity with other Autism RD pages.
  void claims;
  void updateReportContent;
  void refreshReportContent;

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden">

        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{ pointerEvents: 'none' }}
          />
        )}

        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
          {!canEdit && header && (
            <div className="mt-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700">
              Read-only view. Only{assignedClinicianName ? ` ${assignedClinicianName}, the clinician` : ' the clinician'} assigned to the Observation module can review, edit, and approve these paragraphs. You can still preview and download the report from the Documents dashboard.
            </div>
          )}
        </div>

        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                  // Delete is offered only for the optional narrative
                  // paragraphs (Self-Report, Other Behaviours), and never when
                  // the selected one is the ONLY reviewable paragraph left:
                  // isFullyApproved requires totalParagraphs > 0, so deleting
                  // the last one would strand the section on "ready for review"
                  // forever. totalParagraphs already nets off deletedCodes, so
                  // this guard is correct at click time. (An optional paragraph
                  // can be the sole OBS paragraph because criteria only generate
                  // once at least one evidence decision exists.)
                  canDelete={
                    !isEditing
                    && isDeletableParagraph(state.selectedCode)
                    && totalParagraphs > 1
                  }
                  onDelete={handleDeleteParagraph}
                />
              </div>

              {/* Right side — AI Inputs panel showing the observation data that
                  was fed to the LLM for the currently selected paragraph. */}
              <div className={`
                w-2/5 p-4 flex flex-col
                ${!state.selectedCode ? 'hidden' : ''}
              `}>
                <div className={`
                  flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto
                  border shadow-sm
                  flex flex-col h-full
                `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput('Observation evidence', state.clinicianDecisions)}
                          {renderModelInput('Clinician notes', state.clinicianInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation dialog for deleting an optional paragraph (Self-Report or
          Other Behaviours). Copy is per-paragraph and notes the paragraph can
          return if the relevant notes/tags change, matching the "resurrect on
          input change only" backend behaviour. */}
      <ParagraphDeleteDialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) setPendingDeleteCode(null);
        }}
        onConfirm={executeDeleteParagraph}
        title={
          (DELETABLE_PARAGRAPH_DIALOG[pendingDeleteCode]
            || DELETABLE_PARAGRAPH_DIALOG[ASD_OBS_SELF_REPORT_CODE]).title
        }
        description={
          (DELETABLE_PARAGRAPH_DIALOG[pendingDeleteCode]
            || DELETABLE_PARAGRAPH_DIALOG[ASD_OBS_SELF_REPORT_CODE]).description
        }
        confirmLabel="Delete"
      />
    </Layout>
  );
}
